import type { AtTag } from "../util/tag.js";
import type { AbsolutePath, ValidatorType } from "shelving";
import { ALLOW_STRING, BOOLEAN, DATA, INTEGER, KEY, KeySchema, NON_NEGATIVE_INTEGER, StateError, StringSchema, TIMESTAMP, pickProps } from "shelving";
import { KEYWORD_FIELDS } from "../util/keyword.js";
import { OPTIONAL_PHOTO } from "../util/media.js";
import { APPROVED_PRIVACIES, PRIVATE_PRIVACIES, PUBLIC_PRIVACIES, type PrivacyTypes } from "../util/privacy.js";
import { COUNTER_SCHEMA, EMPTY_COUNTER } from "./counter.js";
import { ENTITIES } from "./entity.js";
import { ABOUT, OPTIONAL_LOCATION, OPTIONAL_WEBSITE } from "./misc.js";
import { UsernameSchema } from "./username.js";

// Constants.
export const PROFILE_MAX = 48;
export const FOLLOWING_MAX = 48;
export const POINTS_CHALLENGE_PROGRESS = 10;
export const POINTS_CHALLENGE_CLAIM = 100;
export const LOVE_POST_CREATE = 100;
export const LOVE_COMMENT_CREATE = 50;
export const LOVE_REACTION_CREATE = 10;

/**
 * User profile data
 * Stores information that is visible on a user's profile page.
 *
 * - This document is always public.
 * - The `profile.isPublic` setting controls whether the user's badges are public and posts are public by default.
 *
 * - Each user CAN read their own document in this collection (and all subcollections).
 * - Other users MAY read docuements *only if* one of the following is true...
 *   1. The profile is public (`public` is true)
 *   2. They are an approved follower of the profile (listed in `approvedFollowers`)
 */
export const PROFILE_SCHEMA = DATA({
	verified: BOOLEAN,
	username: new UsernameSchema({ description: `Set a unique username for your profile.` }), // Formatted username (including capital letters and underscores) shown in the URL and @tag for a user.
	name: new StringSchema({ title: "Name", max: 40 }),
	about: ABOUT,
	tags: ENTITIES,
	location: OPTIONAL_LOCATION,
	website: OPTIONAL_WEBSITE,
	followers: NON_NEGATIVE_INTEGER,
	following: NON_NEGATIVE_INTEGER,
	photo: OPTIONAL_PHOTO,
	background: new KeySchema({ title: "Profile background", value: "trajectory-pink" }),
	points: COUNTER_SCHEMA,
	love: COUNTER_SCHEMA,
	created: TIMESTAMP,
	...KEYWORD_FIELDS,
});
export type ProfileData = ValidatorType<typeof PROFILE_SCHEMA>;

/** Get the title for a profile. */
export const getProfileTitle = ({ username, name }: ProfileData): string => (name ? `${name} (@${username})` : `@${username}`);

/** Get the path for a profile. */
export const getProfilePath = ({ username }: ProfileData): AbsolutePath => `/${username}`;

/** Get the tag for a profile. */
export const getProfileTag = ({ username }: ProfileData): AtTag => `@${username}`;

/** Profile settings schema only allows a subset of the profile fields to be edited. */
export const PROFILE_FORM_SCHEMA = DATA(pickProps(PROFILE_SCHEMA.props, "name", "location", "website", "about", "photo", "background"));
export type ProfileFormData = ValidatorType<typeof PROFILE_FORM_SCHEMA>;

/** Allowed statuses for a relationship. */
export const FOLLOWING_STATUS = {
	requested: "Requested",
	approved: "Approved",
	blocked: "Blocked",
};
export type FollowingStatus = keyof typeof FOLLOWING_STATUS;

/**
 * List of followings for profiles.
 * - Stored in DB at `followers/{follower}:{followed}`
 * - Follower is first because potential hotspotting is lower (i.e. a user might follow thousands, but might be followed by millions).
 * - Auth UID must match `follower.followed` or `follower.follower`
 */
export const FOLLOWING_SCHEMA = DATA({
	followed: KEY, // UID of the user who is being followed
	follower: KEY, // UID of the user who is doing the following.
	updated: INTEGER, // Date of the last status change.
	status: ALLOW_STRING(FOLLOWING_STATUS),
});
export type FollowingData = ValidatorType<typeof FOLLOWING_SCHEMA>;
export type NonFollowingData = { status: "none" } & { [K in Exclude<keyof FollowingData, "status">]?: undefined };
export const NON_FOLLOWING: NonFollowingData = { status: "none" };
export type YouFollowingData = { status: "you" } & { [K in Exclude<keyof FollowingData, "status">]?: undefined };
export const YOU_FOLLOWING: YouFollowingData = { status: "you" };

export function getFollowingAllowedPrivacyTypes({ status }: FollowingData | NonFollowingData | YouFollowingData): PrivacyTypes {
	return status === "you" ? PRIVATE_PRIVACIES : status === "approved" ? APPROVED_PRIVACIES : PUBLIC_PRIVACIES;
}

/** Get the ID of a following document, or throw if the follower and followed users are the same. */
export function getFollowingID(followed: string, follower: string): string {
	const id = getOptionalFollowingID(followed, follower);
	if (id) return id;
	throw new StateError("Followed and follower users must be different", followed);
}

/** Get the ID of a following document, or `undefined` if the follower and followed users are the same. */
export function getOptionalFollowingID(followed: string, follower: string): string | undefined {
	return follower === followed ? undefined : `${follower}:${followed}`;
}

export const EMPTY_PROFILE = {
	verified: false,
	name: "",
	location: "",
	website: null,
	about: "",
	tags: [],
	followers: 0,
	following: 0,
	photo: null,
	background: "none",
	love: EMPTY_COUNTER,
	points: EMPTY_COUNTER,
} satisfies Partial<ProfileData>;
